import { setAppcuesIdentifier, setFullStoryIdentifier, trackAppcuesEvent } from "@expert/monitoring";
import { appcuesBaseAttributes } from "@expert/monitoring/src/appcues";
import { useEffect, useRef } from "react";
import { usePartner } from "@/sdk";
import { useAgentStore } from "@/sdk/agent/store/agent.store";
import { userCache } from "@/shared-utils/auth/userCache";

/**
 * Hook to safely initialize monitoring tools like FullStory and Appcues
 * Ensures tools are only initialized once and handles proper user identification
 */
export function useInitExpertMonitoringTools() {
    const agentId = useAgentStore((state) => state.agentId);
    const agentName = useAgentStore((state) => state.agentName);
    const partner = usePartner();

    // SSO User information from cache
    const { employeeId, userEmail, identity, givenName, familyName, groups: activeDirectoryGroups } = userCache;

    // Ref to track initialization status
    const isInitialized = useRef(false);

    // Update appcues when partner changes
    useEffect(() => {
        appcuesBaseAttributes.partner = partner;
    }, [partner]);

    // Initialize monitoring tools once with current user information
    useEffect(() => {
        // Skip if already initialized
        if (isInitialized.current) return;

        const initMonitoringTools = () => {
            // FullStory and Appcues init methods have their own internal error handling,

            // Initialize FullStory
            setFullStoryIdentifier({
                identity: agentName,
                displayName: agentName,
                properties: {
                    agentId,
                    email: userEmail,
                    employeeId,
                    activeDirectoryGroups,
                },
            });

            // Initialize Appcues
            setAppcuesIdentifier({
                identity: employeeId ?? agentId,
                name: givenName && familyName ? `${givenName} ${familyName}` : agentName,
                email: userEmail ?? identity,
                partner,
                activeDirectoryGroups,
            });

            // Track initial event
            trackAppcuesEvent("Workspace Loaded", { partner });

            // Mark as initialized
            isInitialized.current = true;
        };

        // Execute initialization once
        initMonitoringTools();
    }, [agentName, agentId, userEmail, employeeId, identity, givenName, familyName, partner, activeDirectoryGroups]);

    // Return initialization status and monitoring info
    return {
        isInitialized: isInitialized.current,
    };
}
